import { Component, OnInit } from '@angular/core';
import {
  UtilService,
  DialogService,
  NotificationService,
  TranslationService,
  PagedResult,
  PagedSearch,
  DatagridService,
} from '../../shared/index';
import { NavigationService } from '../../shared/navigation/navigation.service';
import { CreditCardService } from '../shared/credit-card/credit-card.service';
import { CreditCardGateway } from '../shared/credit-card-gateway/credit-card-gateway.model';
import { CreditCardGatewayService } from '../shared/credit-card-gateway/credit-card-gateway.service';
import { CreditCardTransactionDetail } from '../shared/credit-card/credit-card-transaction-detail.model';
import { CreditCardTransactionSearch } from '../shared/credit-card/credit-card-transaction-search.model';
import { CreditCardTransactionSearchResult } from '../shared/credit-card/credit-card-transaction-search-result.model';
import { CreditCardRefundRequest } from '../shared/credit-card/credit-card-refund-request.model';
import { CreditCardRefundResponse } from '../shared/credit-card/credit-card-refund-response.model';
import { AdjustmentTypeService } from '../shared/adjustment-types/adjustment-type.service';
import { AdjustmentType } from '../shared/adjustment-types/adjustment-type.model';
import { InvoiceLineItem } from '../shared/invoice/invoice-lineitem.model';

@Component({
  selector: 'wl-credit-card-transactions',
  templateUrl: 'credit-card-transactions.component.html',
  providers: [CreditCardService, CreditCardGatewayService, AdjustmentTypeService],
    standalone: false
})
export class CreditCardTransactionsComponent implements OnInit {
  public _utilService: UtilService;
  public _notifyService: NotificationService;
  public _translationService: TranslationService;
  public _creditCardService: CreditCardService;
  public _dialogService: DialogService;
  public _creditCardGatewayService: CreditCardGatewayService;
  public _navigationService: NavigationService;
  public _dataGridService: DatagridService;
  public _adjustmentTypeService: AdjustmentTypeService;

  public searching: boolean;
  public searchPerformed: boolean;
  public searchHasResults: boolean;
  public searchResult: CreditCardTransactionSearchResult;
  public searchParams: CreditCardTransactionSearch;
  public startDate: Date;
  public endDate: Date;
  public creditCardGateways: Array<CreditCardGateway>;
  public selectedGatewayId: number;
  public creditCardPaymentLineItems: Array<InvoiceLineItem>;
  public showLineItems: boolean;
  public isFetchingLineItems: boolean;
  public showModal = false;
  public selectedTransaction: CreditCardTransactionDetail;
  public refundRequest: CreditCardRefundRequest;
  public refundResponse: CreditCardRefundResponse;

  public adjustmentTypes: Array<AdjustmentType>;
  public selectedAdjustmentTypeId: number;

  public constructor(
    utilService: UtilService,
    notifyService: NotificationService,
    translationService: TranslationService,
    creditCardService: CreditCardService,
    dialogService: DialogService,
    creditCardGatewayService: CreditCardGatewayService,
    navigationService: NavigationService,
    dataGridService: DatagridService,
    adjustmentTypeService: AdjustmentTypeService
  ) {
    this._utilService = utilService;
    this._notifyService = notifyService;
    this._translationService = translationService;
    this._creditCardService = creditCardService;
    this._dialogService = dialogService;
    this._creditCardGatewayService = creditCardGatewayService;
    this._navigationService = navigationService;
    this._dataGridService = dataGridService;
    this._adjustmentTypeService = adjustmentTypeService;

    this.searchResult = new CreditCardTransactionSearchResult();
    this.searching = false;
    this.startDate = new Date();
    this.endDate = new Date();
    this.creditCardGateways = new Array<CreditCardGateway>();
    this.selectedGatewayId = 0;
    this.selectedTransaction = new CreditCardTransactionDetail();
    this.searchParams = new CreditCardTransactionSearch();
    this.showLineItems = false;
    this.creditCardPaymentLineItems = [];
    this.isFetchingLineItems = false;

    this.searchPerformed = false;
    this.searchHasResults = false;

    this.refundRequest = new CreditCardRefundRequest();
    this.refundResponse = new CreditCardRefundResponse();

    this.adjustmentTypes = new Array<AdjustmentType>();
    this.selectedAdjustmentTypeId = 0;
  }

  public ngOnInit() {
    this._creditCardGatewayService.getCreditCardGateways().then((data: Array<CreditCardGateway>) => {
      this.creditCardGateways = data;
      if (data.length > 0) {
        this.selectedGatewayId = data[0].CCGatewayID;
      }
    });

    this._adjustmentTypeService.getAdjustmentTypes().then((data: Array<AdjustmentType>) => {
      this.adjustmentTypes = data;
      if (data.length > 0) {
        this.selectedAdjustmentTypeId = data[0].AdjustmentTypeId;
      }
    });
  }

  public executeSearch = (event: any): void => {
    this.searchParams.GatewayId = this.selectedGatewayId;

    this._dataGridService.mapDatagridPagingToPagedSearch(event, this.searchParams);

    //this._dialogService.showWaitIndicator();
    this.searching = true;

    this._creditCardService.searchCreditCardPayments(this.searchParams).then(
      (data: any) => {
        this.searchResult = data;
        //update the search params based on current page number etc
        this._dataGridService.mapPagedResultsToPagedSearch(data, this.searchParams);
        if (data.TotalCount > 0) {
          this.searchHasResults = true;
        } else {
          this.searchHasResults = false;
        }
        this.searchPerformed = true;
        this.searching = false;
      },
      (error: any) => {
        this.searchPerformed = true;
        this.searching = false;
      }
    );
  };

  public getTransactionDetail = (tran: CreditCardTransactionDetail): void => {
    this.selectedTransaction = tran;
    this.creditCardPaymentLineItems = [];
    this.showLineItems = false;
    this.showModal = true;
  };

  public getLineItems = (transactionId: number): void => {
    if (this.showLineItems) {
      this.showLineItems = false;
      return;
    }

    this.isFetchingLineItems = true;

    this._creditCardService.getTransactionInvoiceLineItems(transactionId).then(
      lineItems => {
        this.creditCardPaymentLineItems = lineItems;
        this.showLineItems = true;
        this.isFetchingLineItems = false;
      },
      (error: any) => {
        this.isFetchingLineItems = false;
      }
    );
  };

  public onGatewayChange(data: string) {
    //the data comes thru as a string convert to int
    this.selectedGatewayId = parseInt(data);
  }

  public onAdjustmentTypeChange(data: string) {
    //the data comes thru as a string convert to int
    this.selectedAdjustmentTypeId = parseInt(data);
  }

  public onRefundAmountChange($event: any) {
    if (this.refundRequest.RefundAmount > this.selectedTransaction.Amount) {
      this.refundRequest.RefundAmount = this.selectedTransaction.Amount;
    }
  }

  public refundCreditCardTransaction = (): void => {
    if (this.refundRequest.RefundAmount > 0) {
      this.refundRequest.CreditCardTransactionId = this.selectedTransaction.Id;
      this.refundRequest.AdjustmentTypeId = this.selectedAdjustmentTypeId;
      this.refundRequest.CreateAdjustments = true;

      this._creditCardService.refundCreditCardPayment(this.refundRequest).then((data: CreditCardRefundResponse) => {
        this.refundResponse = data;
      });
    }
  };

  public refundIsValid = (): boolean => {
    var valid = false;
    if (
      this.refundRequest.RefundAmount > 0 &&
      this.selectedAdjustmentTypeId > 0 &&
      this._utilService.isNotNullEmptyOrWhitespace(this.refundRequest.RefundReason)
    ) {
      valid = true;
    }

    return valid;
  };
}
